import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { Search, MessageSquare, Building2, Clock, Mail, Phone } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Card, CardContent } from '../../components/ui/card';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminInquiries = () => {
  const { token } = useAuth();
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');

  useEffect(() => {
    const fetchInquiries = async () => {
      try {
        const params = typeFilter !== 'all' ? { inquiry_type: typeFilter } : {};
        const response = await axios.get(`${API_URL}/api/admin/inquiries`, {
          headers: { Authorization: `Bearer ${token}` },
          params
        });
        setInquiries(response.data);
      } catch (error) {
        console.error('Error fetching inquiries:', error);
        toast.error('Failed to load inquiries');
      } finally {
        setLoading(false);
      }
    };
    fetchInquiries();
  }, [token, typeFilter]);

  const filteredInquiries = inquiries.filter(i =>
    i.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    i.company?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getTypeColor = (type) => {
    return type === 'fleet' 
      ? 'bg-blue-500/20 text-blue-400 border-blue-500/30'
      : 'bg-purple-500/20 text-purple-400 border-purple-500/30';
  };

  return (
    <div className="space-y-6" data-testid="admin-inquiries">
      {/* Header */}
      <div>
        <h1 className="font-serif text-3xl text-[#181B1F] font-light">Inquiries</h1>
        <p className="text-gray-500 mt-1">Fleet and long-term rental requests</p>
      </div>

      {/* Filters */}
      <div className="flex gap-4 flex-wrap">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search inquiries..."
            className="input-default pl-10"
            data-testid="search-inquiries"
          />
        </div>
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="input-default w-40" data-testid="filter-type">
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent className="bg-white border-gray-200">
            <SelectItem value="all" className="text-[#181B1F]">All Types</SelectItem>
            <SelectItem value="fleet" className="text-[#181B1F]">Fleet Rental</SelectItem>
            <SelectItem value="long_term" className="text-[#181B1F]">Long-Term</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Inquiries List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-500">Loading inquiries...</p>
        </div>
      ) : filteredInquiries.length === 0 ? (
        <div className="text-center py-12">
          <MessageSquare className="w-12 h-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500">No inquiries found</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredInquiries.map((inquiry, index) => (
            <motion.div
              key={inquiry.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Card className="bg-white border-gray-100 hover:border-gray-200 transition-colors">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-4">
                      <div className="flex items-center gap-3">
                        <Badge className={`${getTypeColor(inquiry.inquiry_type)} border`}>
                          {inquiry.inquiry_type === 'fleet' ? 'Fleet Rental' : 'Long-Term'}
                        </Badge>
                        <span className="text-gray-400 text-sm flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {inquiry.created_at ? format(new Date(inquiry.created_at), 'PPp') : 'Unknown'}
                        </span>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <p className="text-[#181B1F] font-medium">{inquiry.name}</p>
                          {inquiry.company && (
                            <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                              <Building2 className="w-3 h-3" />
                              {inquiry.company}
                            </p>
                          )}
                        </div>
                        <div className="space-y-1">
                          <p className="text-gray-500 text-sm flex items-center gap-2">
                            <Mail className="w-4 h-4 text-gray-400" />
                            {inquiry.email}
                          </p>
                          <p className="text-gray-500 text-sm flex items-center gap-2">
                            <Phone className="w-4 h-4 text-gray-400" />
                            {inquiry.phone}
                          </p>
                        </div>
                      </div>

                      {inquiry.message && (
                        <div className="pt-3 border-t border-gray-100">
                          <p className="text-gray-400 text-xs uppercase tracking-wider mb-2">Message</p>
                          <p className="text-gray-600 text-sm whitespace-pre-line">{inquiry.message}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminInquiries;
